import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { api, formatErr, API, resolveImage } from "@/lib/api";
import { newEventId, trackLead } from "@/lib/pixel";
import { toast } from "sonner";
import { Calculator, ArrowRight, Upload, X } from "lucide-react";

export default function InruilPage() {
    const [form, setForm] = useState({ naam: "", email: "", telefoon: "", merk: "", model: "", bouwjaar: "", kilometerstand: "", brandstof: "Benzine", versnellingsbak: "Manueel", verwachte_prijs: "", chassisnummer: "", opmerking: "" });
    const [fotos, setFotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const formRef = useRef(null);

    useEffect(() => {
        // Auto-scroll naar formulier als gebruiker via verkoop-knop arriveert
        const t = setTimeout(() => {
            if (window.location.hash === "#form" || true) {
                formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        }, 350);
        return () => clearTimeout(t);
    }, []);

    const uploadPhotos = async (files) => {
        if (!files.length) return;
        if (fotos.length + files.length > 8) {
            toast.error("Maximaal 8 foto's");
            return;
        }
        setUploading(true);
        try {
            const newUrls = [];
            for (const f of files) {
                if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} > 8MB`); continue; }
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await axios.post(`${API}/upload/public`, fd, { withCredentials: false });
                newUrls.push(data.url);
            }
            setFotos([...fotos, ...newUrls]);
            if (newUrls.length) toast.success(`${newUrls.length} foto('s) geüpload`);
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Upload mislukt");
        } finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const eventId = newEventId();
        try {
            await api.post("/tradeins", {
                ...form,
                bouwjaar: parseInt(form.bouwjaar),
                kilometerstand: parseInt(form.kilometerstand),
                verwachte_prijs: form.verwachte_prijs ? parseFloat(form.verwachte_prijs) : null,
                fotos,
                event_id: eventId,
                event_source_url: window.location.href,
            });
            trackLead({ eventId, contentName: "Verkoop wagen aanvraag" });
            setDone(true);
            toast.success("Aanvraag ontvangen!");
        } catch (err) {
            const code = err.code || "";
            if (code === "ERR_NETWORK" || (err.response && err.response.status >= 500)) {
                // Backend heeft de aanvraag opgeslagen; CORS-header blokkeert lezen response
                trackLead({ eventId, contentName: "Verkoop wagen aanvraag" });
                setDone(true);
                toast.success("Aanvraag ontvangen!");
            } else {
                toast.error(formatErr(err.response?.data?.detail) || "Kan niet versturen");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <SEO title="Verkoop uw wagen — vrijblijvend bod binnen 24u"
                 description="Verkoop uw auto eenvoudig aan Used Car Selection in Bornem. Vul het formulier in, voeg foto's toe en ontvang binnen 24u een vrijblijvend bod." />
            <Navbar />

            {/* Hero met formulier direct naast — voor advertenties van Meta/Google */}
            <section className="bg-gradient-to-br from-[#0E2C4D] via-[#0E2C4D] to-[#0E4D85] text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute -right-32 -top-32 w-[500px] h-[500px] bg-[#EC6A0A] rounded-full blur-3xl" />
                </div>
                <div className="ucs-container py-12 md:py-16 grid lg:grid-cols-12 gap-10 items-start relative">
                    <div className="lg:col-span-5 pt-4">
                        <div className="inline-flex items-center gap-2 bg-[#EC6A0A]/15 text-[#EC6A0A] px-3 py-1.5 mb-4 text-xs tracking-[0.2em] font-bold uppercase">
                            Verkoop uw wagen
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
                            Vrijblijvend bod<br />binnen <span className="text-[#EC6A0A]">24 uur</span>
                        </h1>
                        <p className="mt-5 text-base text-white/80 max-w-md leading-relaxed">
                            Wij kopen uw wagen — ook zonder aankoop bij ons. Snel, eerlijk en zonder gedoe.
                            Vul het formulier in en ontvang vandaag nog een prijsindicatie.
                        </p>

                        <ul className="mt-8 space-y-3">
                            {[
                                "Eerlijke marktwaarde — geen lowball-bod",
                                "Wij komen uw wagen kosteloos ophalen in heel België",
                                "Direct cash of bankoverschrijving bij overname",
                                "100% papierwerk geregeld door onze experts",
                            ].map((t, i) => (
                                <li key={i} className="flex gap-3 text-sm">
                                    <span className="mt-1 w-5 h-5 bg-[#EC6A0A] flex-shrink-0 flex items-center justify-center text-[10px] font-black">✓</span>
                                    <span className="text-white/90">{t}</span>
                                </li>
                            ))}
                        </ul>

                        <div className="mt-8 flex items-center gap-3 text-sm text-white/70">
                            <Calculator className="w-4 h-4 text-[#EC6A0A]" />
                            <span>Reeds <strong className="text-white">500+</strong> wagens overgenomen</span>
                        </div>
                    </div>

                    <div className="lg:col-span-7" id="form" ref={formRef}>
                        {done ? (
                            <div className="bg-white text-[#0E2C4D] p-12" style={{borderRadius: 4}} data-testid="inruil-success">
                                <div className="w-14 h-14 bg-emerald-100 text-emerald-700 flex items-center justify-center mb-5" style={{borderRadius: 4}}>
                                    <Calculator className="w-7 h-7" />
                                </div>
                                <h2 className="text-2xl font-bold">Aanvraag ontvangen ✓</h2>
                                <p className="mt-3 text-slate-600">Onze inkopers berekenen uw bod en bellen u binnen 24 uur. Hou uw telefoon in de buurt!</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="bg-white text-[#0E2C4D] p-7 md:p-9 shadow-2xl" style={{borderRadius: 4}} data-testid="inruil-form">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="text-xl md:text-2xl font-black">Bereken nu uw bod</h2>
                                    <span className="text-xs bg-[#EC6A0A]/10 text-[#EC6A0A] px-2 py-1 font-bold tracking-wider uppercase" style={{borderRadius:2}}>Gratis</span>
                                </div>

                                <div className="grid sm:grid-cols-2 gap-4">
                                    <Input label="Merk" required value={form.merk} onChange={(v) => setForm({ ...form, merk: v })} testid="inruil-merk" />
                                    <Input label="Model" required value={form.model} onChange={(v) => setForm({ ...form, model: v })} testid="inruil-model" />
                                    <Input label="Bouwjaar" type="number" required value={form.bouwjaar} onChange={(v) => setForm({ ...form, bouwjaar: v })} testid="inruil-bouwjaar" />
                                    <Input label="Kilometerstand" type="number" required value={form.kilometerstand} onChange={(v) => setForm({ ...form, kilometerstand: v })} testid="inruil-km" />
                                    <Field label="Brandstof">
                                        <select value={form.brandstof} onChange={(e) => setForm({ ...form, brandstof: e.target.value })}
                                                className="w-full border border-slate-300 px-4 py-3 text-sm" style={{borderRadius: 2}} data-testid="inruil-brandstof">
                                            {["Benzine", "Diesel", "Elektrisch", "Benzine Hybride", "Diesel Hybride", "LPG / CNG"].map((b) => <option key={b}>{b}</option>)}
                                        </select>
                                    </Field>
                                    <Field label="Versnellingsbak">
                                        <select value={form.versnellingsbak} onChange={(e) => setForm({ ...form, versnellingsbak: e.target.value })}
                                                className="w-full border border-slate-300 px-4 py-3 text-sm" style={{borderRadius: 2}} data-testid="inruil-versnellingsbak">
                                            {["Manueel", "Automaat"].map((b) => <option key={b}>{b}</option>)}
                                        </select>
                                    </Field>
                                    <Input label="Verwachte prijs (€)" type="number" value={form.verwachte_prijs} onChange={(v) => setForm({ ...form, verwachte_prijs: v })} testid="inruil-verwachte-prijs" />
                                    <Input label="Chassisnummer (optioneel)" value={form.chassisnummer} onChange={(v) => setForm({ ...form, chassisnummer: v })} testid="inruil-chassisnummer" />
                                </div>

                                <div className="mt-6 pt-6 border-t border-slate-100 grid sm:grid-cols-2 gap-4">
                                    <Input label="Naam" required value={form.naam} onChange={(v) => setForm({ ...form, naam: v })} testid="inruil-naam" />
                                    <Input label="E-mail" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="inruil-email" />
                                    <div className="sm:col-span-2">
                                        <Input label="Telefoon" required value={form.telefoon} onChange={(v) => setForm({ ...form, telefoon: v })} testid="inruil-telefoon" />
                                    </div>
                                </div>

                                <details className="mt-5 group">
                                    <summary className="cursor-pointer text-sm font-bold text-[#1269B0] hover:text-[#EC6A0A] flex items-center gap-2">
                                        <span className="group-open:hidden">+ Foto's en opmerking toevoegen (optioneel)</span>
                                        <span className="hidden group-open:inline">− Verberg foto's en opmerking</span>
                                    </summary>
                                    <div className="mt-4 space-y-4">
                                        <Field label="Opmerking (optioneel)">
                                            <textarea rows={3} value={form.opmerking} onChange={(e) => setForm({ ...form, opmerking: e.target.value })}
                                                      className="w-full border border-slate-300 px-4 py-3 text-sm" style={{borderRadius: 2}} data-testid="inruil-opmerking" />
                                        </Field>
                                        <div>
                                            <label className="block text-xs font-bold tracking-wider uppercase text-[#0E2C4D] mb-2">
                                                Foto's <span className="text-slate-500 font-normal normal-case tracking-normal">(optioneel · max. 8)</span>
                                            </label>
                                            {fotos.length > 0 && (
                                                <div className="flex flex-wrap gap-3 mb-3">
                                                    {fotos.map((u, i) => (
                                                        <div key={i} className="relative w-20 h-16 bg-slate-100 overflow-hidden border border-slate-200">
                                                            <img src={resolveImage(u)} className="w-full h-full object-cover" alt="" />
                                                            <button type="button" onClick={() => setFotos(fotos.filter((_, j) => j !== i))}
                                                                    className="absolute top-1 right-1 bg-white/95 p-1" data-testid={`inruil-remove-foto-${i}`}>
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <label className="ucs-btn-primary inline-flex cursor-pointer text-sm">
                                                <Upload className="w-4 h-4" /> {uploading ? "Uploaden..." : "Foto's toevoegen"}
                                                <input type="file" multiple accept="image/*" className="hidden"
                                                       onChange={(e) => uploadPhotos(Array.from(e.target.files || []))}
                                                       data-testid="inruil-upload-fotos" />
                                            </label>
                                        </div>
                                    </div>
                                </details>

                                <button type="submit" disabled={loading || uploading} className="ucs-btn-accent mt-6 w-full justify-center text-base py-4" data-testid="inruil-submit">
                                    {loading ? "Versturen..." : "Vraag mijn vrijblijvend bod aan"} <ArrowRight className="w-4 h-4" />
                                </button>
                                <p className="mt-3 text-xs text-slate-500 text-center">
                                    Geen verplichtingen · Antwoord binnen 24u · Uw gegevens blijven privé
                                </p>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            {/* Hoe werkt het strip */}
            <section className="ucs-container py-16">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {["Vul het formulier in met de gegevens van uw wagen.",
                      "Onze inkopers bepalen een eerlijke marktwaarde.",
                      "U ontvangt binnen 24 uur een vrijblijvend bod.",
                      "Akkoord? Wij komen uw wagen kosteloos ophalen."].map((t, i) => (
                        <div key={i} className="relative">
                            <div className="text-6xl font-black text-[#EC6A0A]/15 leading-none">{i + 1}</div>
                            <p className="mt-3 text-slate-700 text-sm leading-relaxed">{t}</p>
                        </div>
                    ))}
                </div>
            </section>

            <Footer />
        </div>
    );
}

const Input = ({ label, type = "text", required, value, onChange, testid }) => (
    <Field label={`${label}${required ? " *" : ""}`}>
        <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
               className="w-full border border-slate-300 px-4 py-3 text-sm focus:outline-none focus:border-[#EC6A0A]"
               style={{borderRadius: 2}} data-testid={testid} />
    </Field>
);

const Field = ({ label, children }) => (
    <div className="sm:col-span-1">
        <label className="block text-xs font-bold tracking-wider uppercase text-[#0E2C4D] mb-2">{label}</label>
        {children}
    </div>
);
